<template>
  <div class="main column gap-3 justify-content-center">
    <div class="column gap-1 text-align-left">
      <p class="body xl font-bold">{{ t('login.title') }}</p>
      <p class="body m font-regular">
        {{ t('login.form-message') }}
      </p>
    </div>
    <div class="w-100">
      <TextField
        v-model="formUserValue"
        :label="t('login.form.user')"
        :placeHolder="t('login.form.user.placeholder')"
        :required="true"
      />
      <TextField
        v-model="formPasswordValue"
        :label="t('login.form.password')"
        type="password"
        :placeHolder="t('login.form.password.placeholder')"
        :required="true"
      />
    </div>
    <FormButton
      :label="t('login.form.submit')"
      size="large"
      :onClick="loginUser"
      :disabled="isFormDisabled"
      class-name="w-100 "
    />
    <NoAccountSection />
  </div>
</template>

<script setup lang="ts">
import NoAccountSection from '@/components/auth/NoAccountSection.vue';
import FormButton from '@/components/common/button/FormButton.vue';
import TextField from '@/components/common/text-field/TextField.vue';
import { useAuthFlow } from '@/composables/useAuthFlow';
import RouteNames from '@/configuration/router/names.routes';
import navigationService from '@/services/NavigationService';
import { useToasterStore } from '@/stores/useToasterStore';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const toaster = useToasterStore();
const { processFrenchAuth } = useAuthFlow();

const formUserValue = ref<string>('');
const formPasswordValue = ref<string>('');

const isFormDisabled = computed(() => {
  return !formUserValue.value || !formPasswordValue.value;
});

async function loginUser(): Promise<void> {
  try {
    if (!formUserValue.value || !formPasswordValue.value) {
      toaster.error({
        title: t('login.toast.error_general'),
        body: t('login.toast.empty-fields')
      });
      return;
    }

    const isAuthenticated = await processFrenchAuth(
      formUserValue.value,
      formPasswordValue.value
    );

    if (isAuthenticated) {
      navigationService.navigateTo({ name: RouteNames.Home });
    } else {
      throw new Error('Authentication failed');
    }
  } catch (err) {
    if (typeof err === 'string') {
      toaster.error({
        title: t('login.toast.error.title'),
        body: t(`login.toast.${err}`)
      });
      return;
    }

    toaster.error({
      title: t('login.toast.error.title'),
      body: t('login.toast.error')
    });
  }
}
</script>

<style scoped lang="scss">
.main {
  width: inherit;
  height: 100vh;
  position: relative;
  overflow: hidden;
}

.main::before {
  content: '';
  position: fixed;
  top: 0;
  right: 0;
  width: $bg-login-width;
  height: 100%;
  background-image: url('@/assets/bg-design.svg');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: top right;
  z-index: -1;
}
</style>
